import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/routes/routes';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { HelpTip } from '@/pages/fulfillment/components/HelpTip';
import type { ReservationHolder } from '@/infrastructure/FulfillmentAdminClient';

/** What the release does with the freed units — restocking and writing off are different book entries. */
export interface ReleaseDraft {
  reason: string;
  restock: boolean;
  /** Blank means the whole reservation; a partial restock is the rest written off. */
  restockQty: string;
}

export interface ReservedOrdersModalProps {
  sku: string;
  locationCode: string;
  holders: ReservationHolder[];
  loading: boolean;
  /** The read failed, which says nothing at all about who holds the sku. */
  failed: boolean;
  draft: ReleaseDraft;
  busyKey: string | null;
  oversold?: boolean;
  onDraftChange: (draft: ReleaseDraft) => void;
  onRelease: (holder: ReservationHolder) => void;
  onRetry: () => void;
  onClose: () => void;
}

/** What Reserved is and what each choice does to Total, behind the (?) on the title [SSD-7472]. */
const HELP_LINES = [
  'Reserved is how many units on this shelf orders are holding until they ship. Each row below is one order line holding units here.',
  "Release lets go of a hold that should not be there, such as a cancelled or refunded order that kept its hold. It frees that order line's whole hold, on every shelf the line holds, not only this one. Say why in Release reason: it is recorded with the release, and a write-off carries it into Stock History.",
  'Tick Restock the units when the units are on the shelf: they stay in Total and become available. Leave it unticked when they are not there: they are written off and Total goes down, on each shelf the line held. Restock quantity keeps that many and writes off the rest.',
  'A line that is already inbounded or ready to pack cannot be released here; the release is refused.',
];

/** A reservation left on a cancelled order is the phantom this clears, so the reason rides to the ledger [6]. */
export const ReservedOrdersModal = ({
  sku,
  locationCode,
  holders,
  loading,
  failed,
  draft,
  busyKey,
  oversold = false,
  onDraftChange,
  onRelease,
  onRetry,
  onClose,
}: ReservedOrdersModalProps) => {
  const dialog = useModalDialog(onClose);
  const [selected, setSelected] = useState<ReservationHolder | null>(null);
  const kept = selected && draft.restock ? (draft.restockQty.trim() ? Number(draft.restockQty) : selected.qty) : 0;
  const validQuantity =
    selected !== null && Number.isInteger(kept) && kept >= (draft.restock ? 1 : 0) && kept <= selected.qty;

  return (
    <dialog
      className="inv-modal"
      ref={dialog}
      aria-label={`Reserved quantity ${sku}`}
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <header>
        <span>
          Reserved Quantity — {sku} · {locationCode || 'Unassigned'}
          <HelpTip topic="Reserved Quantity" lines={HELP_LINES} />
        </span>
        <button type="button" className="x" aria-label="Close reserved orders" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body">
        {oversold && (
          <div role="alert" aria-label="Stock integrity" className="alert alert-danger note danger text-danger">
            <b>Stock integrity</b> — reserved exceeds stock on sku {sku}. Over-reservation detected.
          </div>
        )}
        <div className="relrow">
          <div className="rel-reason">
            <label htmlFor="release-reason">Release reason</label>
            <input
              id="release-reason"
              className="inp"
              placeholder="Why, e.g. order cancelled, hold left behind"
              value={draft.reason}
              onChange={(event) => onDraftChange({ ...draft, reason: event.target.value })}
            />
          </div>
          <div>
            <label htmlFor="release-restock" className="chk">
              <input
                id="release-restock"
                type="checkbox"
                aria-label="Restock the units"
                checked={draft.restock}
                onChange={(event) => onDraftChange({ ...draft, restock: event.target.checked })}
              />{' '}
              Restock the units
            </label>
          </div>
          {draft.restock && (
            <div className="rel-qty">
              <label htmlFor="release-restock-qty">Restock quantity</label>
              <input
                id="release-restock-qty"
                className="inp"
                type="number"
                min={0}
                placeholder="All"
                value={draft.restockQty}
                onChange={(event) => onDraftChange({ ...draft, restockQty: event.target.value })}
              />
            </div>
          )}
        </div>
        {/* Release stays grey until a reason is written; say so, and keep the line's space so typing does not jump. */}
        {holders.length > 0 && (
          <p className={draft.reason.trim() ? 'rel-hint is-done' : 'rel-hint'}>
            Release turns on once a reason is written.
          </p>
        )}
        {/* Above the list, so six holders or more do not push it below the fold. */}
        <div className="note">
          A reservation still sitting on a cancelled or refunded order is a suspected phantom. Restocking returns the
          units to this shelf; leaving the box unticked writes them off.
        </div>
        <table className="tbl rel-tbl">
          <colgroup>
            <col />
            <col className="rel-qty-col" />
            <col className="rel-act-col" />
          </colgroup>
          <thead>
            <tr>
              <th>Order / line</th>
              <th>Reserved Qty</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {holders.map((holder) => (
              <tr key={`${holder.orderId}-${holder.lineNo}`}>
                <td>
                  <Link to={ROUTES.fulfillment.orderDetail(holder.orderId)}>
                    #{holder.orderId} / line {holder.lineNo}
                  </Link>
                </td>
                <td className="num">{holder.qty}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-red-line btn-sm"
                    disabled={!draft.reason.trim() || busyKey !== null}
                    onClick={() => setSelected(holder)}
                  >
                    Release
                  </button>
                </td>
              </tr>
            ))}
            {/* A failed read must never wear the empty state: "nobody holds this" is a finding, not a silence. */}
            {holders.length === 0 && (
              <tr>
                <td colSpan={3} className="blocked-note">
                  {loading ? (
                    'Loading the holding orders…'
                  ) : failed ? (
                    <>
                      The holding orders could not be read.{' '}
                      <button
                        type="button"
                        className="btn btn-line btn-sm"
                        aria-label="Retry reading the holding orders"
                        onClick={onRetry}
                      >
                        Retry
                      </button>
                    </>
                  ) : (
                    'No order is holding this sku.'
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {selected && (
          <section role="region" aria-label="Confirm reservation release" className="note warn">
            <p>
              Release #{selected.orderId} / line {selected.lineNo} — {sku} · {locationCode}: {selected.qty} reserved
              units.
            </p>
            <p>
              Keep {kept} on shelf · Discard {selected.qty - kept}. Discarding decreases total stock.
            </p>
            <p>Reason: {draft.reason}</p>
            {!validQuantity && (
              <p role="alert">Restock quantity must be a whole number between 1 and {selected.qty}.</p>
            )}
            <button
              type="button"
              className="btn btn-gray btn-sm"
              disabled={busyKey !== null}
              onClick={() => setSelected(null)}
            >
              Cancel release
            </button>{' '}
            <button
              type="button"
              className="btn btn-red-line btn-sm"
              disabled={busyKey !== null || !draft.reason.trim() || !validQuantity}
              onClick={() => {
                const holder = selected;
                setSelected(null);
                onRelease(holder);
              }}
            >
              Confirm release
            </button>
          </section>
        )}
      </div>
      <div className="foot">
        <button type="button" className="btn btn-gray btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
    </dialog>
  );
};
